import React, { useEffect, useState, useCallback } from 'react';
import {
  getSymbolGroups, createSymbolGroup, updateSymbolGroup, deleteSymbolGroup, validateSymbol,
} from '../api';
import Button from './Button';
import Spinner from './Spinner';
import SymbolSuggestion from './SymbolSuggestion';
import TickerAutocomplete from './TickerAutocomplete';

const ASSET_TYPES = [
  { value: 'auto', label: 'Auto' },
  { value: 'stock', label: 'Stock' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'fx', label: 'Forex' },
];

// Mirrors the symbol shape used by the backtest runner: uppercase ticker, and
// FX pairs are 6 letters (e.g. EURUSD). With 'auto' the class is detected by the
// backend, so we only enforce the loose ticker shape and let the probe decide.
function normalizeSymbol(raw, assetType) {
  const sym = (raw || '').trim().toUpperCase();
  if (!sym) return { error: 'Enter a symbol.' };
  if (assetType === 'fx') {
    if (!/^[A-Z]{6}$/.test(sym)) return { error: 'Enter an FX pair as 6 letters, e.g. EURUSD.' };
  } else if (!/^[A-Z0-9.-]{1,15}$/.test(sym)) {
    return { error: 'Enter a valid ticker (letters, digits, “.” or “-”).' };
  }
  return { symbol: sym };
}

/**
 * Self-contained watchlist CRUD (header + create button + grid of cards).
 * Rendered both on the standalone /watchlists page and folded into the
 * Markets page — pass `subtitle` to customize the helper line.
 */
export default function WatchlistsManager({
  subtitle = 'Group symbols into baskets, then run a strategy across a whole basket from the backtest results screen.',
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getSymbolGroups()
      .then((res) => setGroups(res.data || []))
      .catch(() => setError('Could not load your watchlists.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchGroup = (id, next) => {
    setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, ...next } : g)));
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const { data } = await createSymbolGroup({ name: 'New watchlist', symbols: [] });
      setGroups((gs) => [data, ...gs]);
    } catch (e) {
      setError('Could not create the watchlist.');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (group, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === group.name) return;
    patchGroup(group.id, { name: trimmed });
    try {
      await updateSymbolGroup(group.id, { name: trimmed });
    } catch (e) {
      setError('Could not rename the watchlist.');
      load();
    }
  };

  const persistSymbols = async (group, symbols) => {
    patchGroup(group.id, { symbols });
    try {
      await updateSymbolGroup(group.id, { symbols });
    } catch (e) {
      setError('Could not save symbols.');
      load();
    }
  };

  const handleAddSymbol = (group, symbol, assetType) => {
    const exists = (group.symbols || []).some(
      (s) => (s.symbol || '').toUpperCase() === symbol,
    );
    if (exists) return;
    persistSymbols(group, [...(group.symbols || []), { symbol, asset_type: assetType }]);
  };

  const handleRemoveSymbol = (group, symbol) => {
    persistSymbols(
      group,
      (group.symbols || []).filter((s) => (s.symbol || '').toUpperCase() !== symbol),
    );
  };

  const handleDelete = async (group) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete watchlist “${group.name}”?`)) return;
    setGroups((gs) => gs.filter((g) => g.id !== group.id));
    try {
      await deleteSymbolGroup(group.id);
    } catch (e) {
      setError('Could not delete the watchlist.');
      load();
    }
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 8,
      }}>
        <h2 style={{ margin: 0, color: 'var(--text)', fontSize: 20, fontWeight: 700 }}>Watchlists</h2>
        <Button size="sm" onClick={handleCreate} loading={creating}>+ New watchlist</Button>
      </div>
      {subtitle && (
        <p style={{ color: 'var(--muted)', marginTop: 0, marginBottom: 20, fontSize: 14 }}>
          {subtitle}
        </p>
      )}

      {error && (
        <div style={{ marginBottom: 14, color: 'var(--red)', fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : groups.length === 0 ? (
        <div style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>
          No watchlists yet. Create one to get started.
        </div>
      ) : (
        <div style={{
          display: 'grid', gap: 16,
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        }}>
          {groups.map((g) => (
            <WatchlistCard
              key={g.id}
              group={g}
              onRename={handleRename}
              onAddSymbol={handleAddSymbol}
              onRemoveSymbol={handleRemoveSymbol}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WatchlistCard({ group, onRename, onAddSymbol, onRemoveSymbol, onDelete }) {
  const [name, setName] = useState(group.name);
  const [addValue, setAddValue] = useState('');
  // 'auto' by default so users don't have to know a symbol's asset class (matches
  // the backtest runner); the backend resolver detects it. The selector stays for
  // forcing a class to disambiguate collisions (e.g. LINK the stock vs crypto).
  const [assetType, setAssetType] = useState('auto');
  const [addError, setAddError] = useState('');
  const [suggestion, setSuggestion] = useState(null); // resolver response when invalid/ambiguous
  const [checking, setChecking] = useState(false);

  useEffect(() => { setName(group.name); }, [group.name]);

  // Add a symbol whose class is already resolved (from a valid check or a
  // "did you mean" chip), guarding against duplicates.
  const addResolved = (symbol, at) => {
    const exists = (group.symbols || []).some(
      (s) => (s.symbol || '').toUpperCase() === symbol.toUpperCase(),
    );
    if (exists) { setAddError(`${symbol} is already in this list.`); return; }
    onAddSymbol(group, symbol, at);
    setAddValue('');
    setAddError('');
    setSuggestion(null);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const { symbol, error } = normalizeSymbol(addValue, assetType);
    if (error) { setAddError(error); return; }
    const exists = (group.symbols || []).some(
      (s) => (s.symbol || '').toUpperCase() === symbol,
    );
    if (exists) { setAddError(`${symbol} is already in this list.`); return; }
    setAddError('');
    setSuggestion(null);
    // Verify the symbol actually has market data (and, in auto mode, detect its
    // asset class) before adding it.
    setChecking(true);
    try {
      const { data } = await validateSymbol(symbol, assetType);
      if (data?.valid) {
        addResolved(data.symbol || symbol, data.asset_type || assetType);
        return;
      }
      // Invalid or ambiguous — show one-click "did you mean" chips (e.g. XAUUSD
      // typed as a stock -> Forex, or LINK the stock vs crypto) instead of a dead
      // end. The chips carry their own prompt, so only add a red error line when
      // there's nothing to click.
      setSuggestion(data);
      const hasChips = data?.ambiguous || data?.suggested_symbol
        || (Array.isArray(data?.suggestions) && data.suggestions.length);
      if (!hasChips) setAddError(data?.error || `${symbol} doesn't look like a valid symbol.`);
    } catch (err) {
      setAddError('Could not verify that symbol. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const symbols = group.symbols || [];

  return (
    <div style={{
      background: 'var(--panel-2, rgba(255,255,255,0.04))',
      border: '1px solid var(--border, rgba(255,255,255,0.08))',
      borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onRename(group, name)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          aria-label="Watchlist name"
          style={{
            flex: 1, minWidth: 0, background: 'transparent', border: 'none',
            color: 'var(--text)', fontSize: 16, fontWeight: 700, padding: '4px 2px',
            borderBottom: '1px solid transparent', outline: 'none',
          }}
          onFocus={(e) => { e.target.style.borderBottom = '1px solid var(--teal)'; }}
        />
        {group.is_starter && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: 'var(--muted)',
            border: '1px solid var(--border)', borderRadius: 8, padding: '2px 6px',
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>Default</span>
        )}
        <button
          onClick={() => onDelete(group)}
          aria-label="Delete watchlist"
          title="Delete watchlist"
          style={{
            background: 'transparent', border: 'none', color: 'var(--muted)',
            cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4,
          }}
        >🗑</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 28 }}>
        {symbols.length === 0 && (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>No symbols yet.</span>
        )}
        {symbols.map((s) => (
          <span
            key={s.symbol}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(47,214,195,0.12)', color: 'var(--text)',
              border: '1px solid rgba(47,214,195,0.3)', borderRadius: 16,
              padding: '4px 6px 4px 10px', fontSize: 13, fontWeight: 600,
            }}
          >
            {s.symbol}
            {s.asset_type && s.asset_type !== 'stock' && (
              <span style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>
                {s.asset_type}
              </span>
            )}
            <button
              onClick={() => onRemoveSymbol(group, s.symbol)}
              aria-label={`Remove ${s.symbol}`}
              style={{
                background: 'transparent', border: 'none', color: 'var(--muted)',
                cursor: 'pointer', fontSize: 16, lineHeight: 1,
                width: 22, height: 22, borderRadius: '50%',
              }}
            >×</button>
          </span>
        ))}
      </div>

      <form onSubmit={submitAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <TickerAutocomplete
          value={addValue}
          onChange={(val) => { setAddValue(val); setAddError(''); setSuggestion(null); }}
          onPick={(sym, at) => addResolved(sym, at)}
          classes={assetType === 'auto' ? undefined : assetType}
          placeholder={assetType === 'fx' ? 'EURUSD' : 'Add symbol'}
          aria-label="Add symbol"
          wrapperStyle={{ flex: 1, minWidth: 120 }}
          menuMinWidth={240}
          style={{
            width: '100%', boxSizing: 'border-box', background: 'var(--bg, #0b0f14)',
            border: '1px solid var(--border, rgba(255,255,255,0.12))', borderRadius: 8,
            color: 'var(--text)', padding: '8px 10px', fontSize: 14,
          }}
        />
        <select
          value={assetType}
          onChange={(e) => setAssetType(e.target.value)}
          aria-label="Asset type"
          style={{
            background: 'var(--bg, #0b0f14)',
            border: '1px solid var(--border, rgba(255,255,255,0.12))', borderRadius: 8,
            color: 'var(--text)', padding: '8px 10px', fontSize: 14,
          }}
        >
          {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <Button size="sm" type="submit" variant="secondary" loading={checking}>Add</Button>
      </form>
      {addError && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: -4 }}>{addError}</div>}
      {suggestion && !suggestion.valid && (
        <SymbolSuggestion result={suggestion} onApply={(sym, at) => addResolved(sym, at)} compact />
      )}
    </div>
  );
}
